/**
 * Kuhinjski ekran v2 (/kitchen-display-v2): sva logika bez Vue-a i bez DOM-a, da se može provjeriti u Node-u.
 * Plan i brojke: "Dizajn kuhinjskog ekrana.html"; pitanja za backend: docs/2026/10/24_10_2026_Frontend_pitanja_za_backend_kuhinjski_ekran.textile.
 *
 * Tok: izvještaj → normalizeReport() → decorate() (vrijeme stanja i procjena sa ovog uređaja) → filterTickets() →
 * groupByStage(). Sat na kartici daje ticketInfo(), redoslijed sortKey().
 */
import convert from "cyrillic-to-latin";
import { choiceQuantity, orderTotal } from "./guestOrderTotal";

/* ---------- pragovi: prijedlog iz table (D3), kuhinja ih potvrđuje; jedna vrijednost na jednom mjestu ---------- */
export const TH = Object.freeze({
  newWarn: 5, // Naručeno bez roka: žuto od 5 min čekanja
  newLate: 10, // crveno od 10 min
  readyWarn: 5, // Spremno, čeka preuzimanje: žuto od 5 min
  readyLate: 10, // crveno od 10 min
  asap: 30, // U pripremi bez procjene i bez roka: računa se 30 min od narudžbe
  warnShare: 0.8, // U pripremi: žuto kad je prošlo 80% procjene
  schedFar: 60, // do roka više od 60 min = "zakazano" (plavo, bez upozorenja)
  dueWarn: 15, // do roka manje od 15 min = žuto
  minLead: 10, // rok bliži od 10 min od narudžbe nije zakazivanje nego "što prije" (pitanje N5)
  remindAfter: 2, // podsjetnik: narudžba čeka duže od 2 min
  remindEvery: 60, // podsjetnik: tih ton svakih 60 s
  undoMs: 8000, // Poništi poslije svake promjene
  pollMs: 10000, // izvještaj se pita svakih 10 s (pitanje N4)
  staleMs: 30000, // poslije 30 s bez odgovora veza je "prekinuta" (crvena), prije toga "žuta"
  pageMs: 9000, // automatsko listanje (nije podrazumijevano)
  keepMs: 24 * 3600 * 1000, // koliko se pamte vrijeme stanja i procjena po narudžbi
});

export const PREP_OPTIONS = Object.freeze([5, 10, 15, 20, 30, 45]);

/**
 * Ime polja sa napomenom kupca u narudžbi. Dok backend ne potvrdi da polje postoji (pitanje N3), ostaje null i traka
 * sa napomenom se ne crta. Kad dobijete ime, upišite ga ovdje (npr. "note"); kartica i detalji već znaju da je prikažu.
 */
export const NOTE_FIELD = null;

/** Kolone (isti ključevi kao u kdsConstants.KDS_GROUPS) i njihove nijanse: tone → data-c u CSS-u. */
export const STAGES = Object.freeze(
  [
    { key: "pending", tone: "new", states: ["pending", "on-hold"], icon: "mdi-receipt-text-outline", labelKey: "kds_new_orders" },
    { key: "preparing", tone: "prep", states: ["accepted", "in-progress"], icon: "mdi-pot-steam-outline", labelKey: "in-progress" },
    { key: "ready", tone: "ready", states: ["ready"], icon: "mdi-food-takeout-box-outline", labelKey: "ready" },
    { key: "delivered", tone: "done", states: ["delivered"], icon: "mdi-truck-check", labelKey: "delivered" },
    { key: "completed", tone: "comp", states: ["completed"], icon: "mdi-check-all", labelKey: "completed" },
  ].map((stage) => Object.freeze(stage))
);

/** Isto što je do sada: narudžbe u ovim stanjima su na ekranu, ostale (otkazano, odbijeno) nestaju. */
export const ACTIVE_STATES = Object.freeze(["pending", "accepted", "in-progress", "ready", "on-hold", "delivered", "completed"]);

/** Vrsta narudžbe (delivery_type). Nepoznata vrsta se vodi kao "u restoranu", kao na starom ekranu. */
export const TYPES = Object.freeze([
  Object.freeze({ type: 0, icon: "mdi-motorbike", labelKey: "delivery_to_address", shortKey: "type.delivery" }),
  Object.freeze({ type: 1, icon: "mdi-silverware-fork-knife", labelKey: "pickup", shortKey: "type.pickup" }),
  Object.freeze({ type: 2, icon: "mdi-home", labelKey: "consumption_in_restaurant", shortKey: "type.inplace" }),
]);

export const typeMeta = (type) => TYPES[Number(type)] || TYPES[2];

export const stageOfState = (state) => STAGES.find((stage) => stage.states.includes(state)) || null;

/** Faza za pamćenje vremena stanja: accepted i in-progress su ista kolona i isto brojanje. */
const phaseOf = (state) => (state === "in-progress" ? "accepted" : state);

/* ---------- vrijeme i tekst ---------- */
const pad = (n) => String(n).padStart(2, "0");

/** "2026-10-24 21:07:00" (razmak umjesto T) ne razumiju svi preglednici; bez zone se čita kao lokalno vrijeme. */
export function parseTs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = /^\d{4}-\d{2}-\d{2} \d/.test(String(value)) ? String(value).replace(" ", "T") : String(value);
  const ms = new Date(text).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export const fmtClock = (ms) => {
  const d = new Date(ms);
  return pad(d.getHours()) + ":" + pad(d.getMinutes());
};

/** "2026-10-24" iz lokalnog datuma (za parametre izvještaja). */
export const toISODate = (date) => date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());

export const fmtDate = (ms) => {
  const d = new Date(ms);
  return pad(d.getDate()) + "." + pad(d.getMonth() + 1) + "." + d.getFullYear();
};

/** "<1 min", "7 min", "1 h 23 min", "2 h". Jedinice dolaze iz prevoda (min/мин, h/ч). */
export function fmtDur(minutes, units = { min: "min", h: "h" }) {
  const m = Math.max(0, minutes);
  if (m < 1) return "<1 " + units.min;
  const total = Math.floor(m);
  if (total < 60) return total + " " + units.min;
  const h = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? h + " " + units.h + " " + rest + " " + units.min : h + " " + units.h;
}

/** Tekst za pretragu: ćirilica → latinica, mala slova, bez kvačica ("Ćevapi" = "cevapi" = "Цевапи"). */
export function norm(value) {
  return convert(String(value == null ? "" : value))
    .toLowerCase()
    .replace(/đ/g, "dj")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/** Dio teksta koji se poklapa sa upitom, za žutu oznaku; ako se dužina teksta mijenja pri normalizaciji, bez oznake. */
export function highlight(text, query) {
  const str = String(text == null ? "" : text);
  if (!query) return { pre: str, hit: "", post: "" };
  const n = norm(str);
  const at = n.length === str.length ? n.indexOf(query) : -1;
  if (at < 0) return { pre: str, hit: "", post: "" };
  return { pre: str.slice(0, at), hit: str.slice(at, at + query.length), post: str.slice(at + query.length) };
}

/* ---------- izvještaj → kartice ---------- */

/**
 * Rok ("Dostaviti do"): order.date + order.delivery_time. Rok koji je manje od TH.minLead iza narudžbe (ili prije nje)
 * nije zakazivanje nego "što prije" i ne daje rok, da narudžba ne bi odmah bila crvena dok ne odgovorite na pitanje N5.
 */
export function dueOf(order, createdAt) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String((order && order.delivery_time) || ""));
  if (!match || createdAt == null) return null;
  const day = order.date ? String(order.date).slice(0, 10) : "";
  const base = /^\d{4}-\d{2}-\d{2}$/.test(day) ? new Date(day + "T00:00:00") : new Date(createdAt);
  base.setHours(Number(match[1]), Number(match[2]), 0, 0);
  const due = base.getTime();
  return Number.isFinite(due) && due - createdAt >= TH.minLead * 60000 ? due : null;
}

const isPackage = (transaction, choices) =>
  transaction.line_type ? transaction.line_type === "package" : Boolean(transaction.food && transaction.food.is_package) || choices.length > 0;

function itemsOf(order, latin) {
  return (Array.isArray(order.order_guest_transactions) ? order.order_guest_transactions : []).map((transaction) => {
    const choices = (Array.isArray(transaction.prilog) ? transaction.prilog : []).map((choice) => {
      const q = choiceQuantity(transaction, choice);
      return {
        q,
        name: latin((choice.food && choice.food.name) || (choice.meals && choice.meals.name) || choice.name || ""),
        extra: (parseFloat(choice.price) || 0) * q, // doplata za izbor
      };
    });
    return {
      q: Number(transaction.quantity) || 0,
      name: latin((transaction.food && transaction.food.name) || (transaction.meals && transaction.meals.name) || ""),
      pack: isPackage(transaction, choices),
      amount: (parseFloat(transaction.price) || 0) * (Number(transaction.quantity) || 0),
      choices,
    };
  });
}

function ticketOf(order, guest, ctx) {
  const latin = ctx.latin || ((s) => s || "");
  const created = parseTs(order.created_at) ?? ctx.now;
  const first = latin(guest.name || "");
  const last = latin(guest.lastname || "");
  const location = order.location || null;
  const restaurant = order.restaurant && order.restaurant.id != null ? { id: order.restaurant.id, name: latin(order.restaurant.name || "") } : null;
  const items = itemsOf(order, latin);
  const name = `${first} ${last}`.trim();
  const ticket = {
    id: order.id,
    state: order.state,
    type: typeMeta(order.delivery_type).type,
    guestId: guest.id,
    guest: name || "#" + order.id,
    phone: guest.phone || "",
    address: location ? latin(location.address || "") : "",
    apartment: location && location.apartment ? String(location.apartment) : "",
    floor: location && location.floor ? String(location.floor) : "",
    city: location && location.city && location.city.name ? latin(location.city.name) : "",
    restaurant,
    created,
    due: dueOf(order, created),
    items,
    total: orderTotal(order),
    currency: (order.restaurant && order.restaurant.settings && order.restaurant.settings.price) || "",
    note: ctx.noteField && order[ctx.noteField] ? String(order[ctx.noteField]) : "",
    // sa ovog uređaja (decorate): kad je narudžba ušla u trenutno stanje, kad je prihvaćena i koliko je minuta procijenjeno
    since: null,
    acc: null,
    est: null,
    raw: order,
    rawGuest: guest,
  };
  ticket.search = norm(["#" + ticket.id, ticket.id, ticket.guest, ticket.restaurant ? ticket.restaurant.name : "", ...items.map((i) => i.name + " " + i.choices.map((c) => c.name).join(" "))].join(" "));
  return ticket;
}

/**
 * Izvještaj ima oblik { firma: { grupa: [gost, ...] } }; gost ima orders_guest[] i SVE njegove narudžbe se prikazuju
 * (stari ekran je čitao samo prvu, pitanje N5). Ista narudžba se ne ponavlja. ctx: { latin, now, cutoff, noteField }.
 */
export function normalizeReport(report, ctx = {}) {
  const out = new Map();
  const now = ctx.now ?? Date.now();
  const opts = { ...ctx, now };
  Object.values(report || {}).forEach((companyData) => {
    Object.values(companyData || {}).forEach((guests) => {
      (Array.isArray(guests) ? guests : []).forEach((guest) => {
        (Array.isArray(guest && guest.orders_guest) ? guest.orders_guest : []).forEach((order) => {
          if (!order || order.id == null || !ACTIVE_STATES.includes(order.state) || out.has(order.id)) return;
          const ticket = ticketOf(order, guest, opts);
          if (ctx.cutoff != null && ticket.created < ctx.cutoff) return;
          out.set(order.id, ticket);
        });
      });
    });
  });
  return Array.from(out.values());
}

/* ---------- pamćenje sa ovog uređaja (dok backend ne vrati vrijeme stanja i procjenu: N1, N2) ---------- */

export const emptyMemo = () => ({ seen: {}, est: {} });

export function sanitizeMemo(raw) {
  const memo = emptyMemo();
  if (!raw || typeof raw !== "object") return memo;
  Object.keys(raw.seen || {}).forEach((id) => {
    const v = raw.seen[id];
    if (v && typeof v.p === "string" && Number.isFinite(v.at)) memo.seen[id] = { p: v.p, at: v.at };
  });
  Object.keys(raw.est || {}).forEach((id) => {
    const v = raw.est[id];
    if (v && Number(v.m) > 0 && Number.isFinite(v.at)) memo.est[id] = { m: Number(v.m), at: v.at };
  });
  return memo;
}

/**
 * Upisuje kad je ekran PRVI PUT vidio narudžbu u fazi (pending, on-hold, accepted, ready...) i briše stare zapise.
 * hints: { [id]: { phase, at } } iz naše promjene, da vrijeme počne od klika, a ne od sljedećeg odgovora servera.
 * Vraća novi memo ili isti objekat ako se ništa nije promijenilo.
 */
export function reconcileMemo(memo, tickets, now, hints = {}) {
  const seen = { ...memo.seen };
  const est = { ...memo.est };
  let changed = false;
  tickets.forEach((ticket) => {
    const phase = phaseOf(ticket.state);
    const old = seen[ticket.id];
    if (!old || old.p !== phase) {
      const hint = hints[ticket.id];
      seen[ticket.id] = { p: phase, at: hint && hint.phase === phase ? hint.at : now };
      changed = true;
    }
  });
  [seen, est].forEach((map) =>
    Object.keys(map).forEach((id) => {
      if (now - map[id].at > TH.keepMs) {
        delete map[id];
        changed = true;
      }
    })
  );
  return changed ? { seen, est } : memo;
}

/**
 * Dodaje kartici vrijeme stanja, prihvatanje i procjenu: prvo naša nepotvrđena promjena (overrides), pa pamćenje.
 * Vraća NOVE objekte; ulazne kartice (iz izvještaja) ostaju netaknute.
 */
export function decorate(tickets, memo, overrides = {}) {
  return tickets
    .map((ticket) => {
      const ov = overrides[ticket.id];
      const seen = memo.seen[ticket.id];
      const state = ov ? ov.state : ticket.state;
      const since = ov ? ov.since : seen && seen.p === phaseOf(ticket.state) ? seen.at : null;
      let acc = null;
      let minutes = null;
      if (state === "accepted" || state === "in-progress") {
        const e = ov && ov.est !== undefined ? (ov.est ? { m: ov.est, at: ov.acc } : null) : memo.est[ticket.id];
        minutes = e ? e.m : null;
        acc = e ? e.at : ov && ov.acc != null ? ov.acc : since;
      }
      return { ...ticket, state, since, acc, est: minutes };
    })
    .filter((ticket) => ACTIVE_STATES.includes(ticket.state));
}

/** Naša promjena je potvrđena (server vraća isto stanje) ili je istekla (server odlučuje): ukloni je. */
export function pruneOverrides(overrides, serverStateById, now) {
  const next = {};
  let changed = false;
  Object.keys(overrides).forEach((id) => {
    const ov = overrides[id];
    const server = serverStateById[id];
    const confirmed = server !== undefined && phaseOf(server) === phaseOf(ov.state) && ov.phase !== "queued";
    const expired = ov.phase === "sent" && now > ov.until;
    // narudžba koja je nestala iz izvještaja nakon odbijanja/otkazivanja: potvrđeno
    const gone = server === undefined && ov.phase === "sent" && (ov.state === "rejected" || ov.state === "cancelled");
    if (confirmed || expired || gone) changed = true;
    else next[id] = ov;
  });
  return changed ? next : overrides;
}

/* ---------- sat na kartici ---------- */

/**
 * Šta sat pokazuje. Isto kao tabela "Sat i pragovi" u planu.
 *   cls: ok | warn | late | hold | sched | done      key: ključ prevoda kds2.t.<key> i kds2.a.<key>
 *   d: minute (za tekst), p: 0-100 (popunjenost), late: kasni, icon: mdi ikona (ili null)
 */
export function ticketInfo(t, now) {
  const min = (ms) => ms / 60000;
  const st = t.state;
  if (st === "delivered" || st === "completed") return { cls: "done", key: "done", d: 0, at: t.created, p: 0, late: false, icon: null };
  if (st === "on-hold") return { cls: "hold", key: "hold", d: min(now - (t.since || t.created)), p: 0, late: false, icon: "mdi-pause" };
  if (st === "ready") {
    const w = min(now - (t.since || t.created));
    const cls = w >= TH.readyLate ? "late" : w >= TH.readyWarn ? "warn" : "ok";
    return { cls, key: cls === "late" ? "readyLate" : "ready", d: w, p: Math.max(0, Math.min(100, (w / TH.readyLate) * 100)), late: cls === "late", icon: cls === "late" ? "mdi-alert-outline" : "mdi-package-variant-closed" };
  }
  if (st === "pending") {
    if (t.due) {
      const left = min(t.due - now);
      if (left > TH.schedFar) return { cls: "sched", key: "sched", d: Math.ceil(left), p: 0, late: false, icon: "mdi-calendar-clock" };
      if (left < 0) return { cls: "late", key: "dueOver", d: Math.ceil(-left), p: 100, late: true, icon: "mdi-alert-outline" };
      return { cls: left < TH.dueWarn ? "warn" : "ok", key: "due", d: Math.ceil(left), p: Math.max(0, 100 - (left / TH.schedFar) * 100), late: false, icon: "mdi-clock-outline" };
    }
    const w = min(now - t.created);
    const cls = w >= TH.newLate ? "late" : w >= TH.newWarn ? "warn" : "ok";
    return { cls, key: cls === "late" ? "waitLate" : "wait", d: w, p: Math.max(0, Math.min(100, (w / TH.newLate) * 100)), late: cls === "late", icon: cls === "late" ? "mdi-alert-outline" : "mdi-clock-outline" };
  }
  // accepted / in-progress: odbrojavanje do procjene (ili roka, ili 30 min od narudžbe)
  if (t.due && now > t.due) return { cls: "late", key: "dueOver", d: Math.ceil(min(now - t.due)), p: 100, late: true, icon: "mdi-alert-outline" };
  // sa procjenom: odbrojavanje od prihvatanja; bez nje: od narudžbe do roka, a bez roka 30 min (žuto na 24, crveno na 30)
  const start = t.est && t.acc != null ? t.acc : t.created;
  const target = t.est ? start + t.est * 60000 : t.due || t.created + TH.asap * 60000;
  const totalMs = Math.max(60000, target - start);
  const at = Math.max(now, start); // sat kartice kasni do 5 s za satom ekrana: odbrojavanje ne smije biti duže od procjene
  const rest = min(target - at);
  const share = (at - start) / totalMs;
  const late = rest < 0;
  const cls = late ? "late" : share >= TH.warnShare ? "warn" : "ok";
  return { cls, key: late ? "leftOver" : "left", d: Math.ceil(Math.abs(rest)), p: Math.max(0, Math.min(100, share * 100)), late, icon: late ? "mdi-alert-outline" : "mdi-timer-sand" };
}

/** Redoslijed u koloni: najhitnije gore. */
export function sortKey(t, now) {
  if (t.state === "delivered" || t.state === "completed") return -(t.since || t.created);
  if (t.state === "on-hold") return 1e13 + t.created;
  if (t.state === "ready") return t.since || t.created;
  if (t.state === "accepted" || t.state === "in-progress") return t.est && t.acc ? t.acc + t.est * 60000 : t.due || t.created + TH.asap * 60000;
  return t.due || t.created;
}

/** Glavno dugme kartice: u šta se narudžba pomjera jednim dodirom (null = nema). */
export function actionOf(t) {
  if (t.state === "pending" || t.state === "on-hold") return { to: "accepted", key: "accept" };
  if (t.state === "accepted" || t.state === "in-progress") return { to: "ready", key: "ready" };
  if (t.state === "ready") return { to: "delivered", key: "delivered" };
  return null;
}

/** Korak nazad iz menija kartice. */
export function backOf(t) {
  switch (t.state) {
    case "accepted":
    case "in-progress":
    case "on-hold":
      return "pending";
    case "ready":
      return "accepted";
    case "delivered":
      return "ready";
    case "completed":
      return "delivered";
    default:
      return null;
  }
}

/* ---------- liste ---------- */

export const isLate = (t, now) => ticketInfo(t, now).late;

export function countLate(tickets, now) {
  return tickets.reduce((n, t) => n + (isLate(t, now) ? 1 : 0), 0);
}

/** opts: { type: "all"|0|1|2, q: već normalizovan upit, onlyLate, rest: id restorana ili "" } */
export function filterTickets(tickets, opts, now) {
  const { type = "all", q = "", onlyLate = false, rest = "" } = opts || {};
  return tickets.filter(
    (t) =>
      (type === "all" || t.type === type) &&
      (!rest || (t.restaurant && String(t.restaurant.id) === String(rest))) &&
      (!q || t.search.indexOf(q) > -1) &&
      (!onlyLate || isLate(t, now))
  );
}

/** Kartice po kolonama, svaka kolona poređana po hitnosti. */
export function groupByStage(tickets, now) {
  const groups = {};
  STAGES.forEach((stage) => {
    groups[stage.key] = [];
  });
  tickets.forEach((t) => {
    const stage = stageOfState(t.state);
    if (stage) groups[stage.key].push(t);
  });
  STAGES.forEach((stage) => {
    const keyed = groups[stage.key].map((t) => ({ t, k: sortKey(t, now) }));
    keyed.sort((a, b) => a.k - b.k || a.t.id - b.t.id);
    groups[stage.key] = keyed.map((x) => x.t);
  });
  return groups;
}

/** Narudžbe koje su nove od prošlog odgovora (prazan prošli skup = prvi odgovor, bez zvuka). Samo "pending". */
export function findNew(prevIds, tickets) {
  if (!prevIds) return [];
  return tickets.filter((t) => t.state === "pending" && !prevIds.has(t.id));
}

/** Podsjetnik: ima li neprihvaćene narudžbe (bez roka) koja čeka duže od TH.remindAfter minuta. */
export function needsReminder(tickets, now) {
  return tickets.some((t) => t.state === "pending" && !t.due && (now - t.created) / 60000 >= TH.remindAfter);
}

/* ---------- podešavanja (localStorage kds2-settings; prvi put se čitaju stari ključevi, samo za čitanje) ---------- */

export const SETTINGS_KEY = "kds2-settings";
export const DENSITIES = Object.freeze(["full", "compact", "minimal"]);
export const FONT_SCALES = Object.freeze([1, 1.15, 1.3]);

export function defaultSettings() {
  return {
    font: 1,
    sound: false,
    remind: true,
    wake: false,
    quick: false,
    paging: "scroll",
    lastEst: 15,
    dens: { pending: "full", preparing: "full", ready: "full", delivered: "compact", completed: "compact" },
    vis: { pending: true, preparing: true, ready: true, delivered: true, completed: false },
  };
}

export function sanitizeSettings(raw) {
  const out = defaultSettings();
  if (!raw || typeof raw !== "object") return out;
  if (FONT_SCALES.includes(raw.font)) out.font = raw.font;
  ["sound", "remind", "wake", "quick"].forEach((k) => {
    if (typeof raw[k] === "boolean") out[k] = raw[k];
  });
  if (raw.paging === "auto" || raw.paging === "scroll") out.paging = raw.paging;
  if (PREP_OPTIONS.includes(raw.lastEst)) out.lastEst = raw.lastEst;
  STAGES.forEach((stage) => {
    if (raw.dens && DENSITIES.includes(raw.dens[stage.key])) out.dens[stage.key] = raw.dens[stage.key];
    if (raw.vis && typeof raw.vis[stage.key] === "boolean") out.vis[stage.key] = raw.vis[stage.key];
  });
  if (!STAGES.some((stage) => out.vis[stage.key])) out.vis = defaultSettings().vis;
  return out;
}

/** Stari ekran je čuvao kds-font-scale, kds-visible-groups i kds-density-settings; ovdje se samo čitaju. */
export function importOldSettings(read) {
  const out = {};
  const font = read("kds-font-scale");
  if (FONT_SCALES.includes(font)) out.font = font;
  const groups = read("kds-visible-groups");
  if (Array.isArray(groups) && groups.length) out.vis = Object.fromEntries(STAGES.map((stage) => [stage.key, groups.includes(stage.key)]));
  const dens = read("kds-density-settings");
  if (dens && typeof dens === "object") out.dens = dens;
  return out;
}

export function loadSettings(storage) {
  const read = (key) => {
    try {
      const text = storage.getItem(key);
      return text == null ? null : JSON.parse(text);
    } catch (e) {
      return null;
    }
  };
  const own = read(SETTINGS_KEY);
  return sanitizeSettings(own || importOldSettings(read));
}

export const MEMO_KEY = "kds2-memo";
